import React from 'react';
import { Modal, Button, Typography, Tag, Checkbox } from 'antd';
import { motion } from 'framer-motion';
import { SoundOutlined } from '@ant-design/icons';
import './AnnouncementModal.css';
import { formatImageUrl } from '../../services/api';
// Importar Lottie si está disponible
let Lottie = null;
try {
  // eslint-disable-next-line
  Lottie = require('lottie-react').default;
} catch {}

const { Title, Text } = Typography;

const tagColorMap = {
  info: 'purple',
  promo: 'magenta',
  alerta: 'orange',
  sorteo: 'geekblue',
};

const guardarNoMostrar = (id) => {
  try {
    const arr = JSON.parse(localStorage.getItem('anuncios_no_mostrar') || '[]');
    if (!arr.includes(id)) {
      localStorage.setItem('anuncios_no_mostrar', JSON.stringify([...arr, id]));
    }
  } catch {}
};

const AnnouncementModal = ({ visible, anuncio, onClose, bloqueante }) => {
  const [noMostrar, setNoMostrar] = React.useState(false);
  React.useEffect(() => { setNoMostrar(false); }, [anuncio, visible]);
  if (!anuncio) return null;
  const { titulo, mensaje, tipo, imagen, boton_texto, boton_url, fecha_expiracion, fecha_inicio, lottie_json } = anuncio;
  const imageUrl = imagen ? formatImageUrl(imagen) : null;
  const fechaInicio = fecha_inicio ? new Date(fecha_inicio) : null;
  const fechaFin = fecha_expiracion ? new Date(fecha_expiracion) : null;

  // Render de cabecera: Lottie > Imagen > Icono
  let visualHeader = null;
  if (lottie_json && Lottie) {
    visualHeader = (
      <div style={{ width: 90, height: 90, marginLeft: 16 }}>
        <Lottie animationData={lottie_json} loop={true} style={{ width: '100%', height: '100%' }} />
      </div>
    );
  } else if (imageUrl) {
    visualHeader = (
      <img
        src={imageUrl}
        alt="anuncio"
        style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 16, boxShadow: '0 2px 12px #e91e6388', background: '#fff', border: '2px solid #eee', marginLeft: 16 }}
      />
    );
  } else {
    visualHeader = (
      <div style={{ width: 90, height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3e5f5', borderRadius: 16, marginLeft: 16 }}>
        <SoundOutlined style={{ color: '#9c27b0', fontSize: 54 }} />
      </div>
    );
  }

  // Formato de fecha
  const formatFecha = (date) => date ? date.toLocaleString('es-BO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  // Al aceptar
  const handleAceptar = () => {
    if (noMostrar && anuncio.id) {
      guardarNoMostrar(anuncio.id);
    }
    onClose();
  };

  return (
    <Modal
      open={visible}
      onCancel={bloqueante ? undefined : handleAceptar}
      footer={null}
      closable={!bloqueante}
      centered
      width={540}
      className={`announcement-modal afiche-modal ${bloqueante ? 'bloqueante' : ''}`}
      maskClosable={!bloqueante}
      destroyOnClose
      style={{ padding: 0 }}
      bodyStyle={{ padding: 0, background: '#fff', borderRadius: 24 }}
    >
      <motion.div
        initial={{ y: -60, opacity: 0, scale: 0.97 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        transition={{ type: 'spring', stiffness: 120, damping: 18 }}
        className="announcement-content"
        style={{ padding: 0, background: 'transparent', borderRadius: 24 }}
      >
        {/* Cabecera: Título + visual */}
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: '24px 24px 0 24px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Tag color={tagColorMap[tipo] || 'purple'} style={{ fontWeight: 700, fontSize: 15, marginBottom: 8, letterSpacing: 1, background: '#f3e5f5', color: '#9c27b0', border: 'none' }}>
              {tipo?.toUpperCase() || 'INFO'}
            </Tag>
            <Title level={3} style={{
              color: '#9c27b0',
              marginBottom: 0,
              textAlign: 'left',
              fontWeight: 900,
              letterSpacing: 1.1,
              fontFamily: 'Poppins, Playfair Display, sans-serif',
              lineHeight: 1.2,
              wordBreak: 'break-word',
              marginTop: 8
            }}>{titulo}</Title>
          </div>
          {visualHeader}
        </div>
        {/* Descripción */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18 }}
          style={{ padding: '0 32px', marginTop: 18 }}
        >
          <Text style={{
            display: 'block',
            marginBottom: 12,
            fontSize: 17,
            textAlign: 'left',
            color: '#333',
            fontWeight: 500,
            lineHeight: 1.5
          }}>{mensaje}</Text>
        </motion.div>
        {/* Fechas */}
        <div style={{ padding: '0 32px', marginBottom: 8, marginTop: 2 }}>
          <Text type="secondary" style={{ fontSize: 13, color: '#9c27b0' }}>
            {fechaInicio && fechaFin ? (
              <>Vigente: {formatFecha(fechaInicio)} - {formatFecha(fechaFin)}</>
            ) : fechaFin ? (
              <>Hasta: {formatFecha(fechaFin)}</>
            ) : null}
          </Text>
        </div>
        {/* Botón aceptar y check */}
        <div style={{ padding: '0 32px 24px 32px', marginTop: 10 }}>
          <Button
            type="primary"
            size="large"
            onClick={handleAceptar}
            style={{ background: 'linear-gradient(135deg, #e91e63, #9c27b0)', border: 'none', fontWeight: 700, fontSize: 16, letterSpacing: 1, width: '100%', boxShadow: '0 2px 8px #e91e6388' }}
            block
          >
            {boton_texto || 'Aceptar'}
          </Button>
          <Checkbox
            checked={noMostrar}
            onChange={e => setNoMostrar(e.target.checked)}
            style={{ color: '#9c27b0', fontWeight: 600, fontSize: 15, marginTop: 14 }}
          >
            No volver a mostrar este anuncio
          </Checkbox>
        </div>
      </motion.div>
    </Modal>
  );
};

export default AnnouncementModal; 